import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { sortBy } from 'lodash';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  ReplaySubject,
  combineLatest,
  finalize,
  forkJoin,
  of,
  retry,
  switchMap,
  throwError,
} from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { RestapiService, RestapiServiceWithoutTimezone } from '@app/services/restapi.service';
import { SurveysService } from '@app/services/surveys.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import {
  TemplateEditorDialogComponent,
  TemplateEditorDialogData,
  TemplateEditorDialogResult,
} from '@main-application/shared/template-editor-dialog/components/template-editor-dialog/template-editor-dialog.component';
import {
  AreaModel,
  AreaPageElementAny,
  AreaPageElementQuestionModel,
  AreaPageElementQuestionTypes,
  AreaPageElementRatingGroupModel,
  AreaPageModel,
  AreaSurveyModel,
  RestCustomInspectionModel,
  RestTemplateEditorRatingGroup,
  TemplateEditorRatingGroup,
  TemplateEditorRatingGroupItem,
  TemplateEditorSparePartsGroup,
} from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { ConfirmationModalData } from '@shared/interfaces/modal-data';
import {
  RestInspectionTemplateCategoryModel,
  RestTemplateAreaModel,
  RestTemplateModel,
} from '@template/models/rest-template-model.interface';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';
import { ModalsService } from '@ui-components/modals/modals.service';

import { TemplatesCacheService } from './templates-cache.service';
import { getInitialAreaData } from '../models/share-methods.function';

class UploadResult {
  errors: {
    errorType: number;
    message: string;
  }[];
  state: 'Success' | 'Error';
}

export interface TemplatePlaceholder {
  label: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class TemplateEditorDialogService extends PuSubscribable {
  private simpleService: RestapiServiceWithoutTimezone = inject(RestapiServiceWithoutTimezone);
  private restApiService: RestapiService = inject(RestapiService);
  private modalsService: ModalsService = inject(ModalsService);
  private surveysService: SurveysService = inject(SurveysService);
  private snackbarService: SnackbarService = inject(SnackbarService);
  private templatesCacheService: TemplatesCacheService = inject(TemplatesCacheService);
  private dialog: MatDialog = inject(MatDialog);

  private readonly route = 'inspections/';
  private _templatesLoading = new BehaviorSubject<boolean>(false);
  templatesLoading$ = this._templatesLoading.asObservable();
  private _categoriesLoading = new BehaviorSubject<boolean>(false);
  categoriesLoading$ = this._categoriesLoading.asObservable();
  private _templateDeleting = new BehaviorSubject<boolean>(false);
  templateDeleting$ = this._templateDeleting.asObservable();

  private _templateCreating = new BehaviorSubject<boolean>(false);
  templateCreating$ = this._templateCreating.asObservable();

  private _questionIdFocused = new BehaviorSubject<string>(null);
  questionIdFocused$ = this._questionIdFocused.asObservable();
  private _ratingGroups: ReplaySubject<TemplateEditorRatingGroup[]>;
  private _globalRatingGroups: ReplaySubject<TemplateEditorRatingGroup[]>;

  private _sparePartsGroups: ReplaySubject<TemplateEditorSparePartsGroup[]>;

  private _itemFocused = new BehaviorSubject<AreaModel>(null);
  itemFocused$ = this._itemFocused.asObservable();

  reFetchInspectionTemplateList$ = new BehaviorSubject<number>(new Date().getTime());

  requestTemplateCreating = new EventEmitter<void>();

  constructor() {
    super();
  }

  focusQuestion(id: string) {
    this._questionIdFocused.next(id);
  }

  focusItem(item: AreaModel) {
    if (item) {
      this._itemFocused.next(item);
    }
  }

  leaveFocus(id: number) {
    setTimeout(() => {
      if (this._itemFocused.value?.id == id) {
        this._itemFocused.next(null);
      }
    }, 500);
  }

  openTemplateEditorModal(data: TemplateEditorDialogData) {
    return this.dialog.open<TemplateEditorDialogComponent, TemplateEditorDialogData, TemplateEditorDialogResult>(
      TemplateEditorDialogComponent,
      {
        data: data,
        width: '744px',
        maxHeight: '100vh',
        panelClass: 'template-editor-modal-container',
        disableClose: true,
        autoFocus: 'dialog',
      }
    );
  }

  getTemplates(preserveInspectionId?: number) {
    this._templatesLoading.next(true);
    const cache = this.templatesCacheService.getTemplatesCache();
    const action$ = cache?.length
      ? of(cache)
      : this.restApiService
          .getData<RestTemplateModel[]>(
            this.route + 'template/list?filterUnavailableTemplates=false&doNotReturnContent=true'
          )
          .pipe(tap(templates => this.templatesCacheService.updateTemplatesCache([...templates])));
    return action$.pipe(
      map(templates => {
        if (!templates?.length) {
          return [];
        }
        const filteredTemplates = templates.filter(
          template =>
            (template.isAvailable && !template.isDeleted && !template.isScheduledForDeletion) ||
            template.id === preserveInspectionId
        );
        return filteredTemplates.sort((a, b) => {
          if (a.name === b.name) return 0;
          return a.name > b.name ? 1 : -1;
        });
      }),
      finalize(() => {
        this._templatesLoading.next(false);
      }),
      catchError(err => this.handleError(err, SnackbarErrorMessage.LoadingTemplatesList))
    );
  }

  getInspectionTemplateCategories() {
    this._categoriesLoading.next(true);

    return this.restApiService.getData<RestInspectionTemplateCategoryModel[]>('inspectionTemplateCategory').pipe(
      map(templateCategories => {
        if (!templateCategories?.length) {
          return [];
        }
        return templateCategories;
      }),
      finalize(() => {
        this._categoriesLoading.next(false);
      }),
      catchError(err => this.handleError(err, SnackbarErrorMessage.LoadingTemplateCategoriesList))
    );
  }

  private getRatingGroupsInternal(isGlobal: boolean) {
    this.restApiService
      .getData<RestTemplateEditorRatingGroup[]>(this.route + (isGlobal ? 'global/' : '') + 'ratingGroups')
      .pipe(
        map(ratingGroups => {
          return ratingGroups.map(group => {
            const { itemsJson, ...rest } = group;
            return { ...rest, items: JSON.parse(itemsJson) as TemplateEditorRatingGroupItem[] };
          });
        })
      )
      .subscribe({
        next: ratingGroups =>
          isGlobal ? this._globalRatingGroups.next(ratingGroups) : this._ratingGroups.next(ratingGroups),
        error: error => (isGlobal ? this._globalRatingGroups.error(error) : this._ratingGroups.error(error)),
      })
      .untilDestroyed(this);
  }

  private prepareSparePartsGroupsViewInternal(
    sourceData: TemplateEditorSparePartsGroup[]
  ): TemplateEditorSparePartsGroup[] {
    sourceData = sourceData.filter(x => x.propertyOrPortfolioId == null);
    const hashByParentId = sourceData.reduce(function (map, obj) {
      let list: TemplateEditorSparePartsGroup[];
      const key = obj.parentGroupId ?? -1;

      if ((list = map[key]) == null) {
        map[key] = list = [];
      }

      list.push(obj);

      return map;
    }, {});

    const dataToProcess = (hashByParentId[-1] as TemplateEditorSparePartsGroup[]) ?? [];

    for (let counter = 0; counter < dataToProcess.length; counter++) {
      //adding parent to hierarchy name
      const parent = dataToProcess[counter];
      const children = hashByParentId[parent.id] as TemplateEditorSparePartsGroup[];

      if (children != null) {
        children.forEach(child => {
          child.hierarchyPath = (child.hierarchyPath != null ? ' > ' : '') + parent.title + ' > ' + child.title;

          dataToProcess.push(child);
        });
      }
    }

    sourceData
      .filter(item => item.hierarchyPath == null)
      .forEach(item => {
        item.hierarchyPath = item.title;
      });

    return sortBy(sourceData, [group => group.hierarchyPath?.toLowerCase()]);
  }

  private getSparePartsGroupsInternal() {
    this.restApiService
      .getData<TemplateEditorSparePartsGroup[]>('SparePartGroup')
      .subscribe({
        next: sparePartGroups => this._sparePartsGroups.next(this.prepareSparePartsGroupsViewInternal(sparePartGroups)),
        error: error => this._sparePartsGroups.error(error),
      })
      .untilDestroyed(this);
  }

  getRatingGroups(isGlobal: boolean) {
    if (isGlobal) {
      if (!this._globalRatingGroups) {
        this._globalRatingGroups = new ReplaySubject<TemplateEditorRatingGroup[]>(1);
        this.getRatingGroupsInternal(isGlobal);
      }
      return this._globalRatingGroups.asObservable();
    }

    if (!this._ratingGroups) {
      this._ratingGroups = new ReplaySubject<TemplateEditorRatingGroup[]>(1);
      this.getRatingGroupsInternal(false);
    }
    return this._ratingGroups.asObservable();
  }

  getSparePartsGroups() {
    if (!this._sparePartsGroups) {
      this._sparePartsGroups = new ReplaySubject<TemplateEditorSparePartsGroup[]>(1);
      this.getSparePartsGroupsInternal();
    }
    return this._sparePartsGroups.asObservable();
  }

  updateRatingGroup(ratingGroup: AreaPageElementRatingGroupModel, isGlobal: boolean) {
    if (!ratingGroup.ratingGroupId) {
      return EMPTY;
    }
    const restRatingGroup: RestTemplateEditorRatingGroup = {
      id: ratingGroup.ratingGroupId,
      name: ratingGroup.items.map(item => item.item.text).join('/'),
      itemsJson: JSON.stringify(ratingGroup.items),
    };

    return this.restApiService
      .post<number>(this.route + (isGlobal ? 'global/' : '') + 'ratingGroups', restRatingGroup)
      .pipe(
        tap(() => this.getRatingGroupsInternal(isGlobal)),
        catchError(err => this.handleError(err, SnackbarErrorMessage.WhileUpdatingRatingGroup))
      );
  }

  getTemplate(templateId: number) {
    return this.restApiService.getData<RestTemplateModel>(this.route + 'template/' + templateId);
  }

  addArea(area: RestTemplateAreaModel, isGlobal: boolean, reloadAction?: string) {
    const serverRequest = { ...area, id: undefined };
    return this.surveysService.add(serverRequest, isGlobal).pipe(
      retry({ count: 1, delay: 300 }),
      tap(() => this.templatesCacheService.resetTemplatesCache()),
      catchError(err => this.handleTemplateError(err, reloadAction)),
      switchMap(id => this.surveysService.get(id, isGlobal).pipe(map(area => [id, area] as const)))
    );
  }

  removeArea(id: number, isGlobal: boolean) {
    return this.surveysService.delete(id, isGlobal);
  }

  removeTemplate(template: RestTemplateModel) {
    this._templateDeleting.next(true);
    return this.restApiService.post<number>('inspections/template/delete', template.id).pipe(
      catchError(err => {
        if (err?.status === 403) {
          this.snackbarService.error(SnackbarErrorMessage.TemplateEditPermissionRequired);
          return throwError(() => err);
        }
        const errorCode = this.snackbarService.getErrorCodeMsg(err);
        if (errorCode === 'inspection_template_delete_unavailable') {
          this.snackbarService.error(SnackbarErrorMessage.RemovingTemplateInUse);
        } else {
          this.snackbarService.error(errorCode ?? SnackbarErrorMessage.RemovingTemplate);
        }

        return throwError(() => err);
      }),
      tap(() => {
        this.snackbarService.success(`${template.name} deleted`);
        this.templatesCacheService.resetTemplatesCache();
      }),
      finalize(() => {
        this._templateDeleting.next(false);
      })
    );
  }

  updateArea(area: AreaModel, isGlobal: boolean, reloadAction?: string) {
    const { areaSurvey, ...rest } = area;

    const cleanPage = (page: AreaPageModel): AreaPageModel => {
      const { elements, ...restPage } = page;
      const filtered: any = {};

      Object.keys(restPage).forEach(key => {
        if (!/^\d+$/.test(key)) {
          filtered[key] = (restPage as any)[key];
        }
      });

      const usedTitles: string[] = (elements ?? [])
        .map(el => ('title' in el && el.title ? el.title.trim() : null))
        .filter((t): t is string => !!t);

      const formatTitleFromType = (type: AreaPageElementQuestionTypes, hasCustomFieldBinding: boolean): string => {
        let base: string;

        switch (type) {
          case 'checkList':
            base = 'Checklist';
            break;
          case 'dateTime':
            base = 'Date & Time';
            break;
          default:
            base = type.charAt(0).toUpperCase() + type.slice(1);
            break;
        }

        if (hasCustomFieldBinding) {
          base = `Custom Field - ${base}`;
        }

        let counter = 1;
        let final = `${base} (${counter})`;

        while (usedTitles.includes(final)) {
          counter++;
          final = `${base} (${counter})`;
        }

        usedTitles.push(final);
        return final;
      };

      return {
        ...filtered,
        elements: (elements ?? [])
          .map((el: AreaPageElementAny) => {
            if ('title' in el) {
              const question = el as AreaPageElementQuestionModel;
              const hasOtherData = !!(
                question.customFieldBinding ||
                question.defaultValue ||
                (question.options && question.options.length)
              );

              if (!question.title || question.title.trim() === '') {
                if (hasOtherData) {
                  return {
                    ...question,
                    title: formatTitleFromType(question.type, !!question.customFieldBinding),
                  };
                }
                return null;
              }
            }
            return el;
          })
          .filter((el): el is AreaPageElementAny => el !== null),
      };
    };

    const areaSurveyToSave: AreaSurveyModel = {
      ...areaSurvey,
      pages: areaSurvey?.pages.map(page => cleanPage(page)),
    };

    const updatedArea: RestTemplateAreaModel = { ...rest, areaSurveyJson: JSON.stringify(areaSurveyToSave) };

    return this.surveysService
      .update(updatedArea, isGlobal)
      .pipe(catchError(err => this.handleError(err, SnackbarErrorMessage.UpdatingTemplate)));
  }

  updateAreasPosition(areas: AreaModel[], reloadAction: string, isGlobal: boolean) {
    const serverCalls = areas
      .filter((e, i) => {
        const shouldBeUpdated = e.position != i || e.id < 0;
        e.position = i;
        return shouldBeUpdated;
      })
      .map(area => (area.id > 0 ? this.updateArea(area, isGlobal) : this.addArea(area, isGlobal, reloadAction)));
    return forkJoin(serverCalls);
  }

  openRemovalTemplateModal(inspectionTemplate: RestTemplateModel, data: ConfirmationModalData = {}, fromBoard = false) {
    if (fromBoard) {
      return this.modalsService.openConfirmationModal({
        ...{
          title: `Delete ${inspectionTemplate.name}?`,
          titleCss: 'fade-truncate',
          content: `Are you sure you want to delete this template?`,
          confirmColor: 'warn',
        },
        ...data,
      });
    }

    return this.modalsService.openConfirmationModal(
      {
        ...{
          title: `Delete ${inspectionTemplate.name} template?`,
          titleCss: 'fade-truncate',
          content: `This template will be deleted forever and ever. Are you feeling this confident? 💪`,
          cancelLabel: 'Yikes, no!',
          confirmLabel: 'Yes, delete it',
          confirmColor: 'warn',
        },
        ...data,
      },
      360
    );
  }

  // CI1 methods

  addCustomInspectionTemplateWithPredefinedItems(data: RestCustomInspectionModel, isGlobal?: boolean) {
    this._templateCreating.next(true);
    return combineLatest([
      this.restApiService.post<number>(this.route + 'template/addCustomInspection', data),
      this.getRatingGroups(isGlobal ?? false),
    ]).pipe(
      catchError(err => this.handleError(err, SnackbarErrorMessage.AddingTemplate)),
      switchMap(([id, ratingGroups]) => {
        const area: RestTemplateAreaModel = {
          title: `Beautiful Area`,
          inspectionTemplateId: id,
          position: 0,
          areaSurveyJson: JSON.stringify(
            getInitialAreaData(`Beautiful Area`, false, 'Incredible Item', true, ratingGroups[0])
          ),
        } as RestTemplateAreaModel;

        return this.addArea(area, isGlobal ?? false);
      }),
      map(([_areaId, area]) => area.inspectionTemplateId),
      tap(() => this.templatesCacheService.resetTemplatesCache()),
      finalize(() => this._templateCreating.next(false))
    );
  }

  editCustomInspectionTemplate(inspectionId: number, data: RestCustomInspectionModel, reloadAction: string) {
    return this.restApiService
      .post<RestCustomInspectionModel>(this.route + 'template/editCustomInspection/' + inspectionId, data)
      .pipe(
        retry({ count: 1, delay: 300 }),
        tap(() => this.templatesCacheService.resetTemplatesCache()),
        catchError(err => this.handleTemplateError(err, reloadAction))
      );
  }

  editTemplate(data: RestTemplateModel, isGlobal = false) {
    return this.restApiService
      .post<RestTemplateModel>(this.route + (isGlobal ? 'template/global/edit' : 'template/edit'), data)
      .pipe(
        tap(() => this.templatesCacheService.resetTemplatesCache()),
        catchError(err => this.handleError(err, SnackbarErrorMessage.UpdatingTemplate))
      );
  }

  downloadTemplateFile(id: number) {
    return this.simpleService.download(this.route + `template/downloadInspectionTemplateRawReport/${id}`);
  }

  uploadTemplateFile(id: number, data: File) {
    return this.simpleService.upload<UploadResult>(this.route + `template/uploadInspectionTemplateRaw/${id}`, data);
  }

  private handleTemplateError(err: HttpErrorResponse, reloadAction: string): Observable<never> {
    const errorMessage = this.snackbarService.getErrorCodeMsg(err);
    if (errorMessage) {
      if (reloadAction && typeof err?.error?.message === 'string') {
        this.snackbarService.error(
          `Your changes did not save. <a class="text-color white underline" data-toast-action="${reloadAction}">Reload</a>`
        );
      } else {
        this.snackbarService.error(errorMessage);
      }
      return throwError(() => err);
    }
    this.snackbarService.error(SnackbarErrorMessage.UpdatingTemplate);

    return throwError(() => err);
  }

  private handleError(err: HttpErrorResponse, type: SnackbarErrorMessage): Observable<never> {
    console.error(err);
    if (err?.status === 403) {
      this.snackbarService.error(SnackbarErrorMessage.TemplateEditPermissionRequired);
      return throwError(() => err);
    }
    const errorMsg = this.snackbarService.getErrorCodeMsg(err) ?? type;
    this.snackbarService.error(errorMsg);
    return throwError(() => err);
  }

  getTemplatePlaceholders(): Observable<TemplatePlaceholder[]> {
    return this.restApiService.getData<TemplatePlaceholder[]>(this.route + 'template/placeholders').pipe(
      catchError(err => {
        const errorMsg = this.snackbarService.getErrorCodeMsg(err) ?? SnackbarErrorMessage.LoadingTemplatePlaceholders;
        this.snackbarService.error(errorMsg);
        return of([]);
      })
    );
  }
}
